import { axios } from '@vyce/core/src/api';

import config from '../config';
import {
  CreateSite,
  DailySchedule,
  EmployeeActivityLog,
  LocationUserDTO,
  OnSiteTradesData,
  RecentlyRegisteredDTO,
  Site,
  SiteShift,
  SiteShiftRule,
  TimeLogByPeriod,
  TimeLogByShift,
  TimeLogBySite,
  TimeLogByWorker,
  TimeOrderByItem,
  TimePeriodSettings,
  TimeTotalInfoProps,
  TopPerformingTeamMembersLatWeekDTO,
  TrackEventType,
  WorkersWorkedTimeOverview,
  ClockOutOutsideSettings,
} from '../../types';
import {
  ActiveCheckInDTO,
  AddSiteWorkerRequest,
  AddTimePeriodPayload,
  BulkAmendTimeLogsRequest,
  BulkClockOutDTO,
  BulkClockOutPayload,
  BulkEditTimeLogsRequestPayload,
  CheckInWorkerDTO,
  CheckInWorkersRequestProps,
  CheckOutWorkerDTO,
  CheckOutWorkersRequestProps,
  ClockedOutRolesDataRequest,
  CreateShiftRequest,
  CustomLocationField,
  CustomLocationFieldsDetailsDTO,
  CustomLocationFieldsDTO,
  DormantWorkerDTO,
  DormantWorkersRequestProps,
  EditWorkerHoursRequestProps,
  EmployeeLocationsRequest,
  ExportTimeLogsDTO,
  ExportTimeLogsRequestData,
  GetEventsRequest,
  GetEventsRequestPayload,
  GetSitesPayload,
  GetSiteWorkersRequest,
  GetSiteWorkersSearchRequestPayload,
  GetTimeLogsPayload,
  GetTimePeriodsRequest,
  LocationFieldDetailsDTO,
  LocationUsersBarchart,
  ManualClockInOut,
  ReportSettingsDTO,
  ShiftRuleSchema,
  TeamMembersWithoutRateDTO,
  TimeLogDTO,
  TimeLogsByWorkerRequest,
  TimeLogsRequestProps,
  TimeLogTotal,
  UpdatedReportSettingsData,
  UpdateShiftRequest,
  UpdateSiteRequest,
  WorkerLookupDTO,
} from '../types';
import dayjs from 'dayjs';

const GW_URL = `${config.gatewayApiUrl}`;

export const addSiteWorkerRequest = ({ companyId, shiftId, siteId, worker }: AddSiteWorkerRequest) => {
  return axios.post(
    `${GW_URL}/public/v1/time/companies/${companyId}/sites/${siteId}/shifts/${shiftId}/users/add`,
    { workers: [worker] }
  );
};

export const createSiteRequest = (companyId: string, site: CreateSite) => {
  return axios.post(`${GW_URL}/public/v1/time/companies/${companyId}/sites`, site);
};

export const getSitesRequest = (companyId: string, data: GetSitesPayload) => {
  return axios.post(`${GW_URL}/public/v1/time/companies/${companyId}/sites/search`, data);
};

export const getSiteRequest = (companyId: string, siteId: string) => {
  return axios.get(`${GW_URL}/public/v1/time/companies/${companyId}/sites/${siteId}`);
};

export const deleteSiteRequest = (companyId: string, siteId: string) => {
  return axios.delete(`${GW_URL}/public/v1/time/companies/${companyId}/sites/${siteId}/remove`);
};

export const updateSiteRequest = ({ companyId, siteId, site }: UpdateSiteRequest) => {
  return axios.patch(`${GW_URL}/public/v1/time/companies/${companyId}/sites/${siteId}`, site);
};

export const createShiftRequest = ({ companyId, shift, siteId }: CreateShiftRequest) => {
  return axios.post(`${GW_URL}/public/v1/time/companies/${companyId}/sites/${siteId}/shifts`, shift);
};

export const updateShiftRequest = ({ companyId, siteId, shiftId, shift }: UpdateShiftRequest) => {
  return axios.patch(
    `${GW_URL}/public/v1/time/companies/${companyId}/sites/${siteId}/shifts/${shiftId}`,
    shift
  );
};

export const deleteShiftRequest = (companyId: string, siteId: string, shiftId: string) => {
  return axios.delete(`${GW_URL}/public/v1/time/companies/${companyId}/sites/${siteId}/shifts/${shiftId}`);
};

export const updateAllRulesShiftRequest = ({
  companyId,
  rules,
}: {
  companyId: string;
  rules: { rules: SiteShiftRule[] };
}) => {
  return axios.post(`${GW_URL}/public/v1/time/companies/${companyId}/sites/shifts/rules`, rules);
};

export const updateSiteWorkerRequest = ({ companyId, shiftId, siteId, worker }: AddSiteWorkerRequest) => {
  return axios.patch(
    `${GW_URL}/public/v1/time/companies/${companyId}/sites/${siteId}/shifts/${shiftId}/workers/${worker.user_id}`,
    worker
  );
};

export const getSiteWorkersRequest = ({
  companyId,
  payload,
}: {
  companyId: string;
  payload: GetSiteWorkersSearchRequestPayload;
}) => {
  return axios.post(`${GW_URL}/public/v1/time/companies/${companyId}/sites/workers/search`, payload);
};

export const getWorkerSitesRequest = ({
  companyId,
  userId,
  payload,
}: GetSiteWorkersRequest & { userId: string }) => {
  return axios.post<{ items: Site[]; count: number }>(
    `${GW_URL}/public/v1/time/companies/${companyId}/sites/users/${userId}/search`,
    { ...payload, show_deleted: false, only_active: true }
  );
};

export const deleteShiftWorkersRequest = (
  companyId: string,
  siteId: string,
  shiftId: string,
  userIds: string[]
) => {
  return axios.post(
    `${GW_URL}/public/v1/time/companies/${companyId}/sites/${siteId}/shifts/${shiftId}/users/remove`,
    { user_ids: userIds }
  );
};

export const deleteWorkersFromLocationRequest = (companyId: string, siteId: string, userIds: string[]) => {
  return axios.post(`${GW_URL}/public/v1/time/companies/${companyId}/sites/${siteId}/users/remove`, {
    user_ids: userIds,
  });
};

export const deleteAllWorkerShiftsRequest = (companyId: string, userId: string) => {
  return axios.post(`${GW_URL}/public/v1/time/companies/${companyId}/users/${userId}/remove_all_time`);
};

export const getActivityLogEventsRequest = ({ companyId, payload }: GetEventsRequest) => {
  return axios.post(`${GW_URL}/public/v1/time/monitoring/companies/${companyId}/activity_log`, payload);
};

export const getTimePeriodsRequest = ({ companyId, payload }: GetTimePeriodsRequest) => {
  return axios.post(
    `${GW_URL}/public/v1/time/companies/${companyId}/users/${payload.user_id}/time_periods/search`,
    payload
  );
};

export const getEventsRequest = ({ companyId, payload }: GetEventsRequest) => {
  return axios.post(
    `${GW_URL}/public/v1/time/companies/${companyId}/users/${payload.user_id}/events/search`,
    payload
  );
};

export const getMapViewEventsRequest = ({
  companyId,
  siteId,
  eventType,
}: {
  companyId: string;
  siteId?: string | null;
  eventType: TrackEventType;
}) => {
  const now = dayjs().utc(false);
  const gte = now.startOf('day');
  const lte = now.endOf('day');
  return axios.post(`${GW_URL}/public/v1/time/monitoring/companies/${companyId}/map_view`, {
    limit: 5000,
    site_id: siteId,
    time_period: {
      lte: lte.format(),
      gte: gte.format(),
    },
    event_type: eventType,
  });
};

export const manualClockInOutRequest = ({
  companyId,
  userId,
  payload,
}: {
  companyId: string;
  userId: string;
  payload: ManualClockInOut;
}) => {
  return axios.post(`${GW_URL}/public/v1/time/companies/${companyId}/users/${userId}/checks`, payload);
};

export const BULK_CLOCK_OUT_USER_IDS_LIMIT = 2000;

/** Clocks out everyone matching the filters at one time. Nobody matching is a 200 with both lists empty. */
export const bulkClockOutRequest = ({
  companyId,
  payload,
}: {
  companyId: string;
  payload: BulkClockOutPayload;
}) => {
  return axios.post<BulkClockOutDTO>(
    `${GW_URL}/public/v1/time/companies/${companyId}/checks/bulk_check_out`,
    payload
  );
};

export const checkUserActiveCheckInRequest = ({
  companyId,
  userId,
}: {
  companyId: string;
  userId: string;
}) => {
  return axios.post<ActiveCheckInDTO>(
    `${GW_URL}/public/v1/time/monitoring/companies/${companyId}/users/${userId}/active_check_in`
  );
};

export const getLookupSiteWorkersRequest = ({ companyId, payload }: GetSiteWorkersRequest) => {
  return axios.post<{ count: number; items: WorkerLookupDTO[] }>(
    `${GW_URL}/public/v1/time/companies/${companyId}/sites/workers/lookup`,
    payload
  );
};

export const getLocationManagersRequest = ({
  companyId,
  siteId,
  payload,
}: {
  companyId: string;
  siteId: string;
  payload: {
    order_by: {
      field: string;
      desc: boolean;
    }[];
  };
}) => {
  return axios.post<{
    count: number;
    items: {
      user_id: string;
      first_name: string;
      last_name: string;
      avatar: string;
    }[];
  }>(`${GW_URL}/public/v1/time/companies/${companyId}/sites/${siteId}/managers`, payload);
};

export const appointLocationManagersRequest = ({
  companyId,
  siteId,
  userIds,
}: {
  companyId: string;
  siteId: string;
  userIds: string[];
}) => {
  return axios.post(`${GW_URL}/public/v1/time/companies/${companyId}/sites/${siteId}/managers/add`, {
    user_ids: userIds,
  });
};

export const deleteLocationManagersRequest = ({
  companyId,
  siteId,
  userIds,
}: {
  companyId: string;
  siteId: string;
  userIds: string[];
}) => {
  return axios.post(`${GW_URL}/public/v1/time/companies/${companyId}/sites/${siteId}/managers/remove`, {
    user_ids: userIds,
  });
};

export const getCheckInWorkersRequest = ({
  companyId,
  payload,
  signal,
}: {
  companyId: string;
  payload: CheckInWorkersRequestProps;
  signal?: AbortSignal;
}) => {
  return axios.post<{ count: number; items: CheckInWorkerDTO[] }>(
    `${GW_URL}/public/v1/time/monitoring/companies/${companyId}/workers/checked_in`,
    payload,
    { signal }
  );
};

export const getCheckInWorkersRolesRequest = ({
  companyId,
  payload,
}: {
  companyId: string;
  payload: {
    limit: number;
    offset: number;
  };
}) => {
  return axios.post<{ items: string[] }>(
    `${GW_URL}/public/v1/time/monitoring/companies/${companyId}/workers/checked_in/roles`,
    payload
  );
};

export const getCheckOutWorkersRolesRequest = ({
  companyId,
  payload,
}: {
  companyId: string;
  payload: {
    limit: number;
    offset: number;
  };
}) => {
  return axios.post<{ items: string[] }>(
    `${GW_URL}/public/v1/time/monitoring/companies/${companyId}/workers/checked_out/roles`,
    payload
  );
};

export const getCheckOutWorkersRequest = ({
  companyId,
  payload,
  signal,
}: {
  companyId: string;
  payload: CheckOutWorkersRequestProps;
  signal?: AbortSignal;
}) => {
  return axios.post<{ count: number; items: CheckOutWorkerDTO[] }>(
    `${GW_URL}/public/v1/time/monitoring/companies/${companyId}/workers/checked_out`,
    payload,
    { signal }
  );
};

export const getTimeLogsByPeriodRequest = (companyId: string, payload: { next_pivot_date: string }) => {
  return axios.post<{ count: number; items: TimeLogByPeriod[]; next_pivot_date: string }>(
    `${GW_URL}/public/v1/time/time_logs/companies/${companyId}/time_logs_by_period`,
    payload
  );
};

export const getTimeLogsByLocationRequest = (companyId: string, payload: TimeLogsRequestProps) => {
  return axios.post<{ count: number; items: TimeLogBySite[]; totals: TimeTotalInfoProps }>(
    `${GW_URL}/public/v1/time/time_logs/companies/${companyId}/time_logs_by_site`,
    payload
  );
};

export const getTimeLogsByWorkerRequest = (companyId: string, payload: TimeLogsByWorkerRequest) => {
  return axios.post<{ count: number; items: TimeLogByWorker[]; totals: TimeTotalInfoProps }>(
    `${GW_URL}/public/v1/time/time_logs/companies/${companyId}/time_logs_by_worker`,
    payload
  );
};

export const getTimeLogsByShiftRequest = (
  companyId: string,
  userId: string,
  payload: { site_id?: string; start_date: string; end_date: string }
) => {
  return axios.post<TimeLogByShift>(
    `${GW_URL}/public/v1/time/time_logs/companies/${companyId}/time_logs_by_worker_shift?user_id=${userId}`,
    payload
  );
};

export const approveByPeriodRequest = (companyId: string, payload: DailySchedule) => {
  return axios.post(`${GW_URL}/public/v1/time/time_logs/companies/${companyId}/periods/approval`, {
    period: payload,
    approved: true,
  });
};

export const approveBySiteRequest = (companyId: string, siteId: string, payload: DailySchedule) => {
  return axios.post(
    `${GW_URL}/public/v1/time/time_logs/companies/${companyId}/periods/sites/${siteId}/approval`,
    {
      period: payload,
      approved: true,
    }
  );
};

export const approveByWorkerRequest = (
  companyId: string,
  siteId: string,
  userId: string,
  payload: DailySchedule
) => {
  return axios.post(
    `${GW_URL}/public/v1/time/time_logs/companies/${companyId}/periods/sites/${siteId}/users/${userId}/approval`,
    {
      period: payload,
      approved: true,
    }
  );
};

export const getTimeWorkerRequest = (userId: string, companyId: string) => {
  return axios.get(`${GW_URL}/public/v1/time/companies/${companyId}/users/${userId}`);
};

export const editWorkerHoursRequest = ({
  companyId,
  day,
  amendments,
  userId,
  siteId,
  shiftId,
}: EditWorkerHoursRequestProps) => {
  return axios.post(
    `${GW_URL}/public/v1/time/time_logs/companies/${companyId}/periods/${day}/sites/${siteId}/shifts/${shiftId}/users/${userId}`,
    amendments
  );
};

export const postExportTimeLogsRequest = ({ companyId, data }: ExportTimeLogsRequestData) => {
  return axios.post<ExportTimeLogsDTO>(
    `${GW_URL}/public/v1/time/reports/companies/${companyId}/time_logs/export`,
    data
  );
};

export const setPeriodSettingsRequest = (companyId: string, payload: TimePeriodSettings) => {
  return axios.put(`${GW_URL}/public/v1/time/companies/${companyId}/period_settings`, payload);
};

export const getPeriodSettingsRequest = (companyId: string) => {
  return axios.get(`${GW_URL}/public/v1/time/companies/${companyId}/period_settings`);
};

export const postWorkersWorkedTimeOverviewRequest = ({
  companyId,
  payload,
}: {
  companyId: string;
  payload: { approved: boolean };
}) => {
  return axios.post<{ count: number; items: WorkersWorkedTimeOverview[] }>(
    `${GW_URL}/public/v1/time/time_logs/companies/${companyId}/widgets/workers_worked_time_overview`,
    payload
  );
};

export const getTopPerformingTeamMembersLatWeekRequest = ({ companyId }: { companyId: string }) => {
  return axios.get<TopPerformingTeamMembersLatWeekDTO>(
    `${GW_URL}/public/v1/time/time_logs/companies/${companyId}/widgets/top_performing_workers_last_week`
  );
};

export const getTimeLogsRequest = (companyId: string, userId: string, payload: GetTimeLogsPayload) => {
  return axios.post<{ count: number; items: TimeLogDTO[]; totals: TimeLogTotal }>(
    `${GW_URL}/public/v1/time/companies/${companyId}/users/${userId}/time_logs/search`,
    payload
  );
};

export const getUserActivityLogEventsRequest = ({
  companyId,
  userId,
  payload,
}: {
  companyId: string;
  userId: string;
  payload: GetEventsRequestPayload;
}) => {
  return axios.post<{ items: EmployeeActivityLog[] }>(
    `${GW_URL}/public/v1/time/companies/${companyId}/users/${userId}/activity_log`,
    payload
  );
};

export const addTimePeriodRequest = (companyId: string, payload: AddTimePeriodPayload) => {
  return axios.post<{ count: number; items: TimeLogDTO[]; totals: TimeLogTotal }>(
    `${GW_URL}/public/v1/time/companies/${companyId}/time_periods`,
    payload
  );
};

export const getTimeModulePreferencesRequest = (companyId: string) => {
  return axios.get(`${GW_URL}/public/v1/time/companies/${companyId}/preferences`);
};

export const updateTimeModulePreferencesRequest = (companyId: string, countAutoClockOut: boolean) => {
  return axios.put(`${GW_URL}/public/v1/time/companies/${companyId}/preferences`, {
    count_auto_check_out: countAutoClockOut,
  });
};

export const updateClockOutOutsideSettingsRequest = (
  companyId: string,
  countClockOutOutsideSettings: ClockOutOutsideSettings
) => {
  return axios.post(`${GW_URL}/public/v1/time/companies/${companyId}/sites/clock_out_settings`, {
    clock_out_settings: countClockOutOutsideSettings,
  });
};

export const getWorkerTimeLogsByShiftRequest = (
  companyId: string,
  userId: string,
  period: { site_id?: string; start_date: string; end_date: string }
) => {
  return axios.post<TimeLogByShift>(
    `${GW_URL}/public/v1/time/companies/${companyId}/users/${userId}/time_logs/time_logs_by_worker_shift`,
    period
  );
};

export const getCustomLocationDetailsFields = ({ companyId }: { companyId: string }) => {
  return axios.get<CustomLocationFieldsDetailsDTO>(
    `${GW_URL}/public/v1/time/reports/companies/${companyId}/sites/fields`
  );
};

export const updateCustomLocationDetailsFields = ({
  companyId,
  data,
}: {
  companyId: string;
  data: { fields: LocationFieldDetailsDTO[] };
}) => {
  return axios.put<CustomLocationFieldsDetailsDTO>(
    `${GW_URL}/public/v1/time/reports/companies/${companyId}/sites/fields`,
    data
  );
};

export const getExportingTimeLogsSettings = ({ companyId }: { companyId: string }) => {
  return axios.get<ReportSettingsDTO>(
    `${GW_URL}/public/v1/time/reports/companies/${companyId}/report_settings`
  );
};

export const updateExportingTimeLogsSettings = ({
  companyId,
  data,
}: {
  companyId: string;
  data: UpdatedReportSettingsData;
}) => {
  return axios.put<ReportSettingsDTO>(
    `${GW_URL}/public/v1/time/reports/companies/${companyId}/report_settings`,
    data
  );
};

export const restoreExportingTimeLogsSettings = ({ companyId }: { companyId: string }) => {
  return axios.get<ReportSettingsDTO>(
    `${GW_URL}/public/v1/time/reports/companies/${companyId}/report_settings/restore`
  );
};

export const getCustomLocationFields = ({ companyId, siteId }: { companyId: string; siteId: string }) => {
  return axios.get<CustomLocationFieldsDTO>(
    `${GW_URL}/public/v1/time/reports/companies/${companyId}/sites/${siteId}/fields/values`
  );
};

export const updateCustomLocationFields = ({
  companyId,
  siteId,
  data,
}: {
  companyId: string;
  siteId: string;
  data: { values: Omit<CustomLocationField, 'name'>[] };
}) => {
  return axios.put<CustomLocationFieldsDTO>(
    `${GW_URL}/public/v1/time/reports/companies/${companyId}/sites/${siteId}/fields/values`,
    data
  );
};

export const getOnSiteTradesDataRequest = (
  companyId: string,
  payload: { site_ids?: string[] },
  signal: AbortSignal
) => {
  return axios.post<OnSiteTradesData>(
    `${GW_URL}/public/v1/time/time_logs/companies/${companyId}/widgets/on_site_trades`,
    payload,
    { signal }
  );
};

export const deleteTimeLogRequest = (companyId: string, workday: string, userId: string) => {
  return axios.delete(`${GW_URL}/public/v1/time/companies/${companyId}/workday/${workday}/users/${userId}`);
};

export const getLocationUsersRequest = ({
  companyId,
  data,
  signal,
}: {
  companyId: string;
  data: {
    offset: number;
    limit: number;
    site_ids?: string[];
    status?: string;
    substring?: string;
  };
  signal?: AbortSignal;
}) => {
  return axios.post<{
    count: number;
    items: LocationUserDTO[];
    unique_users_count: number;
    users_statuses: LocationUsersBarchart;
  }>(`${GW_URL}/public/v1/time/monitoring/companies/${companyId}/location/workers`, data, { signal });
};

export const getClockedOutRolesRequest = ({
  companyId,
  data,
}: {
  companyId: string;
  data: ClockedOutRolesDataRequest;
}) => {
  return axios.post<{ items: string[] }>(
    `${GW_URL}/public/v1/time/monitoring/companies/${companyId}/workers/checked_out/roles`,
    data
  );
};

export const getDidntClockOutRolesRequest = ({
  companyId,
  payload,
}: {
  companyId: string;
  payload: ClockedOutRolesDataRequest;
}) => {
  return axios.post<{ items: string[] }>(
    `${GW_URL}/public/v1/time/monitoring/companies/${companyId}/workers/did_not_clock_out/roles`,
    payload
  );
};

export const getDidntClockedOutWorkersRequest = ({
  companyId,
  payload,
  signal,
}: {
  companyId: string;
  payload: CheckOutWorkersRequestProps;
  signal?: AbortSignal;
}) => {
  return axios.post<{ count: number; items: CheckOutWorkerDTO[] }>(
    `${GW_URL}/public/v1/time/monitoring/companies/${companyId}/workers/did_not_clock_out`,
    payload,
    { signal }
  );
};

export const getDormantWorkersRequest = ({
  companyId,
  payload,
  signal,
}: {
  companyId: string;
  payload: DormantWorkersRequestProps;
  signal?: AbortSignal;
}) => {
  return axios.post<{ count: number; items: DormantWorkerDTO[] }>(
    `${GW_URL}/public/v1/time/monitoring/companies/${companyId}/workers/dormant`,
    payload,
    { signal }
  );
};

export const getWorkerShiftsRequest = ({
  companyId,
  userId,
  siteId,
}: {
  companyId: string;
  userId: string;
  siteId: string;
}) => {
  return axios.post<{ count: number; items: SiteShift[] }>(
    `${GW_URL}/public/v1/time/monitoring/companies/${companyId}/users/${userId}/shifts/search`,
    {
      limit: 1000,
      offset: 0,
      order_by: [{ field: 'name', desc: true }],
      site_id: siteId,
    }
  );
};

export const getEmployeeLocationsRequest = ({ companyId, userId, payload }: EmployeeLocationsRequest) => {
  return axios.post(`${GW_URL}/public/v1/time/companies/${companyId}/users/${userId}/sites/search`, payload);
};

export const getTeamMembersWithoutRateRequest = ({
  companyId,
  payload,
}: {
  companyId: string;
  payload: { limit: number; offset: number; order_by: { field: string; desc: boolean }[] };
}) => {
  return axios.post<{ count: number; items: TeamMembersWithoutRateDTO[] }>(
    `${GW_URL}/public/v1/time/monitoring/companies/${companyId}/workers/no_rate`,
    payload
  );
};

export const getCompaniesWorkersWidgetSharedData = <T>({
  companyId,
  target,
  payload,
}: {
  companyId: string;
  target: string;
  payload: T;
}) => {
  return axios.post<string>(
    `${GW_URL}/public/v1/time/monitoring/companies/${companyId}/workers/${target}/export_to_xlsx`,
    payload,
    {
      headers: {
        accept: 'application/json',
      },
      responseType: 'blob',
    }
  );
};

export const getCompaniesWidgetsSharedData = <T>({
  companyId,
  target,
  payload,
}: {
  companyId: string;
  target: string;
  payload: T;
}) => {
  return axios.post<string>(
    `${GW_URL}/public/v1/time/time_logs/companies/${companyId}/widgets/${target}/export_to_xlsx`,
    payload,
    {
      headers: {
        accept: 'application/json',
      },
      responseType: 'blob',
    }
  );
};

export const getTopPerformingWorkersLastWeekSharedData = ({ companyId }: { companyId: string }) => {
  return axios.get<string>(
    `${GW_URL}/public/v1/time/time_logs/companies/${companyId}/widgets/top_performing_workers_last_week/export_to_xlsx`,
    {
      headers: {
        accept: 'application/json',
      },
      responseType: 'blob',
    }
  );
};

export const getCompaniesLocationWorkersSharedData = <T>({
  companyId,
  data,
}: {
  companyId: string;
  data: T;
}) => {
  return axios.post<string>(
    `${GW_URL}/public/v1/time/monitoring/companies/${companyId}/location/workers/export_to_xlsx`,
    data,
    {
      headers: {
        accept: 'application/json',
      },
      responseType: 'blob',
    }
  );
};

export const getRecentlyRegisteredRequest = ({
  companyId,
  payload,
  signal,
}: {
  companyId: string;
  payload: {
    offset: number;
    limit: number;
    team_ids?: string[];
  };
  signal?: AbortSignal;
}) => {
  return axios.post<{ count: number; items: RecentlyRegisteredDTO[] }>(
    `${GW_URL}/public/v1/time/monitoring/companies/${companyId}/recently_registered_time_users`,
    payload,
    { signal }
  );
};

export const getRecentlyRegisteredSharedData = ({
  companyId,
  payload,
}: {
  companyId: string;
  payload: {
    offset: number;
    limit: number;
    team_ids?: string[];
  };
}) => {
  return axios.post<string>(
    `${GW_URL}/public/v1/time/monitoring/companies/${companyId}/recently_registered_time_users/export_to_xlsx`,
    payload,
    {
      headers: {
        accept: 'application/json',
      },
      responseType: 'blob',
    }
  );
};

export const getShiftRulesData = ({
  companyId,
  payload,
}: {
  companyId: string;
  payload: {
    offset: number;
    limit: number;
    order_by: TimeOrderByItem[];
  };
}) => {
  return axios.post<{ items: ShiftRuleSchema[]; count: number }>(
    `${GW_URL}/public/v1/time/companies/${companyId}/sites/shift_rules/search`,
    payload
  );
};

export const bulkEditTimeLogsRequest = (payload: BulkEditTimeLogsRequestPayload) => {
  return axios.post(
    `${GW_URL}/public/v1/time/time_periods/companies/${payload.company_id}/time_periods/bulk_recalculation`,
    payload
  );
};

export const bulkAmendTimeLogsRequest = ({
  company_id,
  workday,
  site_id,
  shift_id,
  user_id,
  payload,
}: BulkAmendTimeLogsRequest) => {
  return axios.post(
    `${GW_URL}/public/v1/time/time_logs/companies/${company_id}/periods/${workday}/sites/${site_id}/shifts/${shift_id}/bulk_amendments?user_id=${user_id}`,
    payload
  );
};

export const getLocationsWithGeoRequest = (companyId: string) => {
  return axios.get(`${GW_URL}/public/v1/time/companies/${companyId}/sites/with_geo_position`);
};
